/**
 * Adaptateur - RealSecurgenCardReaderAdapter
 *
 * Intégration réelle avec le service FingerAuth (http://localhost:9000/Authentication)
 * Utilise les APIs du lecteur de carte à puce Securgen/Identiv
 *
 * CORRECTIONS APPLIQUÉES:
 * - cardPresent() retourne 0/1/2 : on teste >= 1 au lieu de === 1
 * - Décodage Base64 des données carte (FingerImpl encode tout en Base64)
 * - Attente de l'authentification (cardPresent === 2) avant de lire les données chiffrées
 * - Fallback robuste si getCardData() échoue (utilise le numéro de carte)
 * - Force stop+start pour ré-authentifier la carte (corrige 6A86 après enrollment)
 * - Parsing du format binaire carte avec séparateurs de contrôle
 */

import { ICardReaderService, CardReadResult } from '../../application/ports/output/ICardReaderService';

const ENV = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env ?? {};
const FINGERAUTH_BASE_URL = ENV.VITE_FINGERAUTH_BASE_URL ?? '';
const FINGERAUTH_ENDPOINT = '/Authentication';

export class RealSecurgenCardReaderAdapter implements ICardReaderService {
  private cardPresent = false;
  private selectedReader: string | null = null;

  /**
   * Démarre le service de lecture de carte
   * Accepte result=1 (démarré) ou result déjà démarré (Biocard renvoie 1 dans les deux cas)
   */
  private async startService(): Promise<void> {
    const url = `${FINGERAUTH_BASE_URL}${FINGERAUTH_ENDPOINT}/start`;

    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error(`Échec démarrage service carte: ${response.status}`);
    }

    const result = await response.json();
    if (result !== 1) {
      throw new Error('Service de carte n\'a pas pu démarrer');
    }

    console.log('[Securgen] Service démarré');
  }

  /**
   * Liste les lecteurs de cartes disponibles
   */
  private async getReaders(): Promise<string[]> {
    const url = `${FINGERAUTH_BASE_URL}${FINGERAUTH_ENDPOINT}/getReaders`;

    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error(`Échec récupération lecteurs: ${response.status}`);
    }

    const result = await response.text();

    if (result.includes('Aucun') || result.includes('No reader') || result === '') {
      return [];
    }

    try {
      const parsed = JSON.parse(result);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    } catch {
      // Pas du JSON, essayer le format CSV
    }

    return result.split(',').map(r => r.trim()).filter(Boolean);
  }

  /**
   * Sélectionne un lecteur spécifique
   */
  private async selectReader(reader: string): Promise<void> {
    const url = `${FINGERAUTH_BASE_URL}${FINGERAUTH_ENDPOINT}/selectReader`;

    const response = await fetch(url, {
      method: 'POST',
      cache: 'no-store',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ reader }),
    });

    if (!response.ok) {
      throw new Error(`Échec sélection lecteur: ${response.status}`);
    }

    const result = await response.json();
    if (result !== 1) {
      throw new Error('Échec sélection du lecteur');
    }

    this.selectedReader = reader;
    console.log(`[Securgen] Lecteur sélectionné: ${reader}`);
  }

  /**
   * Appelle /authenticateCard — l'endpoint de référence utilisé par LoginPage.
   *
   * Côté backend, cet endpoint :
   * 1. Lance un thread d'authentification
   * 2. Attend 3 secondes (le thread fait Work() → auth mutuelle → SAESKey)
   * 3. Arrête le thread
   * 4. Redémarre le service (fingerService.start())
   * 5. Retourne cardPresent() → 0/1/2
   *
   * IMPORTANT: Après retour, le service est redémarré et un NOUVEAU thread
   * est lancé. L'auth mutuelle se fait en arrière-plan. Il faut ensuite
   * attendre que cardPresent retourne 2 avant de lire les données.
   */
  private async authenticateCard(): Promise<number> {
    const url = `${FINGERAUTH_BASE_URL}${FINGERAUTH_ENDPOINT}/authenticateCard`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        cache: 'no-store',
      });

      if (!response.ok) {
        return 0;
      }

      const result = await response.json();
      console.log('[Securgen] authenticateCard résultat:', result);
      return typeof result === 'number' ? result : 0;
    } catch {
      return 0;
    }
  }

  /**
   * Vérifie l'état de présence de la carte (0/1/2)
   */
  private async checkCardPresentRaw(): Promise<number> {
    const url = `${FINGERAUTH_BASE_URL}${FINGERAUTH_ENDPOINT}/cardPresent`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        cache: 'no-store',
      });

      if (!response.ok) return 0;

      const result = await response.json();
      return typeof result === 'number' ? result : 0;
    } catch {
      return 0;
    }
  }

  /**
   * Attend que la carte soit complètement authentifiée (cardPresent === 2).
   *
   * Après authenticateCard, le service Biocard est redémarré et un nouveau thread
   * fait Work() (authentification mutuelle ECDH + dérivation SAESKey) en arrière-plan.
   * Il faut attendre que ce processus soit terminé avant de lire les données.
   */
  private async waitForCardAuthenticated(timeoutMs = 15000): Promise<boolean> {
    const startTime = Date.now();

    return new Promise((resolve) => {
      const checkInterval = setInterval(async () => {
        if (Date.now() - startTime > timeoutMs) {
          clearInterval(checkInterval);
          console.warn('[Securgen] Timeout en attendant l\'authentification de la carte');
          resolve(false);
          return;
        }

        const status = await this.checkCardPresentRaw();
        console.log(`[Securgen] cardPresent polling: ${status}`);

        if (status === 2) {
          clearInterval(checkInterval);
          console.log('[Securgen] Carte authentifiée (cardPresent=2)');
          resolve(true);
        } else if (status === 0) {
          clearInterval(checkInterval);
          console.warn('[Securgen] Carte retirée pendant l\'authentification');
          resolve(false);
        }
        // status === 1 : auth en cours, on continue d'attendre
      }, 500);
    });
  }


  /**
   * Récupère le numéro de la carte
   * IMPORTANT: L'API retourne le numéro encodé en Base64
   */
  // @ts-expect-error Méthode conservée pour compatibilité future
  private async getCardNumber(): Promise<string> {
    const url = `${FINGERAUTH_BASE_URL}${FINGERAUTH_ENDPOINT}/getCardNum`;

    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error(`Échec lecture numéro carte: ${response.status}`);
    }

    const cardNumBase64 = await response.text();
    return cardNumBase64.trim();
  }

  /**
   * Récupère les données stockées sur la carte
   * IMPORTANT: L'API retourne les données encodées en Base64
   * IMPORTANT: Nécessite que la carte soit authentifiée (SAESKey disponible)
   */
  private async getCardData(): Promise<string> {
    const url = `${FINGERAUTH_BASE_URL}${FINGERAUTH_ENDPOINT}/getCardData`;

    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error(`Échec lecture données carte: ${response.status}`);
    }

    const cardData = await response.text();
    console.log('[Securgen] getCardData raw response:', cardData);
    console.log('[Securgen] Response length:', cardData.length);
    console.log('[Securgen] First 100 chars:', cardData.substring(0, 100));
    return cardData;
  }

  /**
   * Décode une chaîne Base64 en texte UTF-8
   * L'API FingerAuth (FingerImpl) encode toutes les données carte en Base64
   */
  private decodeBase64(base64Str: string): string {
    try {
      const cleaned = base64Str.trim();
      if (!cleaned) return '';

      const binaryString = atob(cleaned);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Retirer les octets nuls de padding (la carte padde avec des 0x00)
      let end = bytes.length;
      while (end > 0 && bytes[end - 1] === 0) {
        end--;
      }

      const decoder = new TextDecoder('utf-8', { fatal: false });
      return decoder.decode(bytes.slice(0, end));
    } catch (error) {
      console.warn('[Securgen] Échec décodage Base64:', error);
      return base64Str; // Retourner tel quel en fallback
    }
  }

  /**
   * Parse les données binaires de la carte (format GTech UCOPEC)
   *
   * Format observé depuis l'enrollment:
   *   [header bytes][nom client][0x10][numéro compte][0x0A][date expiration][0x0A][date émission][0x07][type compte][0x01][code]
   *
   * Les séparateurs sont des caractères de contrôle:
   *   0x0B (VT)  = avant le nom
   *   0x10 (DLE) = avant le numéro de compte
   *   0x0A (LF)  = entre les dates
   *   0x07 (BEL) = avant le type de compte
   *   0x01 (SOH) = avant le code final
   *
   * Retourne: { accountNumber, clientName, expiryDate, issueDate, accountType }
   */
  private parseBinaryCardData(bytes: Uint8Array): {
    accountNumber?: string;
    clientName?: string;
    expiryDate?: string;
    issueDate?: string;
    accountType?: string;
  } | null {
    try {
      // Retirer padding 0x00
      let end = bytes.length;
      while (end > 0 && bytes[end - 1] === 0) {
        end--;
      }
      if (end < 5) return null;

      const data = bytes.slice(0, end);

      // Chercher les séparateurs de contrôle pour extraire les champs
      // On parcourt les bytes et on découpe aux séparateurs
      const fields: string[] = [];
      let fieldStart = 0;

      for (let i = 0; i < data.length; i++) {
        const b = data[i];
        // Séparateurs de contrôle connus
        if (b === 0x0B || b === 0x10 || b === 0x0A || b === 0x07 || b === 0x01) {
          if (i > fieldStart) {
            const fieldBytes = data.slice(fieldStart, i);
            const decoder = new TextDecoder('utf-8', { fatal: false });
            const fieldText = decoder.decode(fieldBytes).trim();
            if (fieldText) {
              fields.push(fieldText);
            }
          }
          fieldStart = i + 1;
        }
      }
      // Dernier champ
      if (fieldStart < data.length) {
        const fieldBytes = data.slice(fieldStart);
        const decoder = new TextDecoder('utf-8', { fatal: false });
        const fieldText = decoder.decode(fieldBytes).trim();
        if (fieldText) {
          fields.push(fieldText);
        }
      }

      console.log('[Securgen] Champs carte extraits:', fields);

      if (fields.length < 2) return null;

      // Identifier les champs par pattern
      const result: {
        accountNumber?: string;
        clientName?: string;
        expiryDate?: string;
        issueDate?: string;
        accountType?: string;
      } = {};

      // Collecter tous les fragments de nom (peuvent être séparés par 0x0A etc.)
      const nameParts: string[] = [];

      for (const field of fields) {
        // Numéro de compte: séquence de chiffres longue (13+ digits)
        if (/^\d{13,}$/.test(field) && !result.accountNumber) {
          result.accountNumber = field;
        }
        // Date: format JJ/MM/AAAA
        else if (/^\d{2}\/\d{2}\/\d{4}$/.test(field)) {
          if (!result.expiryDate) {
            result.expiryDate = field;
          } else if (!result.issueDate) {
            result.issueDate = field;
          }
        }
        // Type de compte: EPARGNE, COURANT, etc. (mot unique tout en majuscules, 4+ chars)
        else if (/^[A-Z]{4,}$/.test(field) && !result.accountType) {
          result.accountType = field;
        }
        // Nom du client: texte alphabétique avec espaces
        // On collecte TOUS les fragments car le nom complet peut être
        // séparé par des caractères de contrôle (ex: "YAO" [0x0A] "AMENAN ROSE")
        else if (/^[A-Za-zÀ-ÿ\s'-]{2,}$/.test(field)) {
          nameParts.push(field);
        }
      }

      // Concaténer tous les fragments de nom
      if (nameParts.length > 0) {
        result.clientName = nameParts.join(' ');
      }

      if (result.accountNumber || result.clientName) {
        console.log('[Securgen] Données carte parsées:', result);
        return result;
      }

      return null;
    } catch (error) {
      console.error('[Securgen] Erreur parsing binaire:', error);
      return null;
    }
  }

  /**
   * Parse les données de la carte pour extraire le numéro de compte
   *
   * CORRECTION: Les données arrivent encodées en Base64 depuis FingerImpl.
   * Il faut d'abord décoder le Base64 pour obtenir les bytes originaux.
   *
   * Supporte 3 formats:
   * 1. Format binaire GTech (séparateurs de contrôle) — format réel observé
   * 2. JSON
   * 3. Format clé:valeur
   */
  // @ts-expect-error Méthode conservée pour compatibilité future
  private parseAccountNumber(cardDataBase64: string, cardNumber: string): string | null {
    try {
      // Étape 1: Décoder le Base64 en bytes bruts
      const cleaned = cardDataBase64.trim();
      if (!cleaned) return cardNumber;

      let rawBytes: Uint8Array;
      try {
        const binaryString = atob(cleaned);
        rawBytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          rawBytes[i] = binaryString.charCodeAt(i);
        }
      } catch {
        console.warn('[Securgen] Données pas en Base64, tentative directe');
        rawBytes = new TextEncoder().encode(cleaned);
      }

      // Étape 2: Tentative parsing binaire GTech (format réel)
      const binaryResult = this.parseBinaryCardData(rawBytes);
      if (binaryResult?.accountNumber) {
        console.log(`[Securgen] Compte trouvé (format binaire): ${binaryResult.accountNumber}`);
        return binaryResult.accountNumber;
      }

      // Étape 3: Décoder en texte pour tentatives JSON / clé:valeur
      const cardData = this.decodeBase64(cardDataBase64);
      console.log('[Securgen] Données carte décodées (texte):', cardData.substring(0, 100));

      if (!cardData) return cardNumber;

      // Tentative JSON
      try {
        const parsed = JSON.parse(cardData);
        if (parsed.accountNumber) return parsed.accountNumber;
        if (parsed.numcpt) return parsed.numcpt;
        if (parsed.compte) return parsed.compte;
      } catch {
        // Pas du JSON
      }

      // Tentative format clé:valeur
      const parts = cardData.split('|');
      for (const part of parts) {
        const sepIndex = part.indexOf(':');
        if (sepIndex > 0) {
          const key = part.substring(0, sepIndex).trim().toLowerCase();
          const value = part.substring(sepIndex + 1).trim();
          if (key && value && (key === 'accountnumber' || key === 'numcpt' || key === 'compte')) {
            return value;
          }
        }
      }

      // Étape 4: Si le nom a été trouvé dans le format binaire,
      // utiliser le numéro de carte comme fallback
      if (binaryResult?.clientName) {
        console.log(`[Securgen] Client trouvé: ${binaryResult.clientName}, fallback numéro carte`);
        return this.decodeBase64(cardNumber);
      }

      // Étape 5: Chercher un numéro de compte dans le texte brut
      const accountMatch = cardData.match(/\d{13,}/);
      if (accountMatch) {
        console.log(`[Securgen] Numéro de compte extrait par regex: ${accountMatch[0]}`);
        return accountMatch[0];
      }

      // Fallback: utiliser le numéro de carte décodé
      console.warn('[Securgen] Format données carte non reconnu, fallback sur numéro de carte');
      return this.decodeBase64(cardNumber);
    } catch (error) {
      console.error('[Securgen] Erreur parsing données carte:', error);
      return null;
    }
  }

  /**
   * Attend l'insertion et l'authentification d'une carte.
   *
   * FLUX ALIGNÉ SUR LoginPage.tsx (projet de référence qui fonctionne) :
   * 1. /start → démarrer le service (ignore si déjà démarré)
   * 2. /getReaders → lister les lecteurs
   * 3. /selectReader → sélectionner le premier lecteur
   * 4. Polling avec /authenticateCard toutes les 1.5s
   *    → cet endpoint force l'auth mutuelle et retourne cardPresent (0/1/2)
   * 5. Quand authenticateCard retourne 1 ou 2 → carte prête
   * 6. Lire getCardNum et getCardData
   */
  async waitForCardInsertion(timeoutMs = 30000): Promise<CardReadResult> {
    const startTime = Date.now();

    try {
      // Étape 1: Démarrer le service (comme LoginPage: ignore si déjà démarré)
      try {
        await this.startService();
      } catch {
        // Service peut être déjà démarré, on continue
        console.log('[Securgen] Service déjà démarré, on continue');
      }

      // Étape 2 & 3: Récupérer et sélectionner le premier lecteur disponible
      const readers = await this.getReaders();
      if (readers.length === 0) {
        return {
          success: false,
          errorCode: 'NO_READER_AVAILABLE',
        };
      }

      if (!this.selectedReader || !readers.includes(this.selectedReader)) {
        await this.selectReader(readers[0]);
      }

      console.log('[Securgen] En attente d\'insertion de carte (via authenticateCard)...');

      // Étape 4: Polling avec /authenticateCard (comme LoginPage)
      // Cet endpoint fait: nouveau thread → Work() → auth mutuelle → stop → start → cardPresent
      return new Promise((resolve) => {
        const checkInterval = setInterval(async () => {
          // Vérifier le timeout
          if (Date.now() - startTime > timeoutMs) {
            clearInterval(checkInterval);
            console.error('[Securgen] Timeout dépassé');
            resolve({
              success: false,
              errorCode: 'CARD_INSERT_TIMEOUT',
            });
            return;
          }

          try {
            // authenticateCard force l'authentification et retourne 1 ou 2 si la carte est prête
            const authResult = await this.authenticateCard();

            if (authResult === 1 || authResult === 2) {
              clearInterval(checkInterval);
              this.cardPresent = true;
              console.log(`[Securgen] Carte détectée (authenticateCard=${authResult})`);

              // Étape 5: ATTENDRE que l'auth mutuelle soit terminée
              // authenticateCard redémarre le service, le thread fait Work() en arrière-plan
              // Il faut attendre cardPresent === 2 avant de lire les données
              console.log('[Securgen] Attente de l\'authentification mutuelle (cardPresent=2)...');
              const authenticated = await this.waitForCardAuthenticated(15000);

              if (!authenticated) {
                console.warn('[Securgen] Auth non terminée dans le délai, tentative de lecture quand même');
              }

              // Étape 6: Lire TOUTES les données depuis getCardData (numCarte inclus en champ 8)
              // Plus besoin d'appeler getCardNum séparément
              let cardNumber = '';
              let accountNumber = '';
              try {
                const cardDataBase64 = await this.getCardData();
                console.log('[Securgen] Calling parseCardDataFields with:', cardDataBase64.substring(0, 50));
                const parsedData = this.parseCardDataFields(cardDataBase64);
                console.log('[Securgen] parseCardDataFields returned:', parsedData);

                if (parsedData) {
                  cardNumber = parsedData.numCarte || '';
                  accountNumber = parsedData.numCarte || '';  // Le numCarte sert d'identifiant CBS
                  console.log(`[Securgen] Données carte lues: numCarte=${cardNumber}, nom=${parsedData.nom}`);
                } else {
                  console.warn('[Securgen] parseCardDataFields returned null!');
                }
              } catch (error) {
                console.error('[Securgen] Impossible de lire les données de la carte:', error);
                resolve({
                  success: false,
                  errorCode: 'CARD_READ_ERROR',
                });
                return;
              }

              if (!cardNumber) {
                console.error('[Securgen] Numéro de carte vide dans getCardData');
                resolve({
                  success: false,
                  errorCode: 'CARD_READ_ERROR',
                });
                return;
              }

              console.log(`[Securgen] Carte insérée: ${cardNumber} -> Compte: ${accountNumber}`);

              resolve({
                success: true,
                accountNumber,
                cardNumber,
              });
            }
          } catch (error) {
            // Erreur lors de la vérification, on continue à essayer
            console.debug('[Securgen] authenticateCard en cours...');
          }
        }, 1500); // Polling toutes les 1.5s (comme LoginPage)
      });
    } catch (error) {
      console.error('[Securgen] Erreur:', error);
      return {
        success: false,
        errorCode: 'SERVICE_ERROR',
      };
    }
  }

  /**
   * Éjecte la carte (n'est généralement pas supporté par les lecteurs PC/SC standards)
   */
  async ejectCard(): Promise<void> {
    console.log('[Securgen] Veuillez retirer la carte manuellement');
    this.cardPresent = false;
  }

  /**
   * Vérifie si une carte est présente
   */
  isCardPresent(): boolean {
    return this.cardPresent;
  }

  /**
   * Vérifie si le lecteur est disponible
   */
  async isAvailable(): Promise<boolean> {
    try {
      const readers = await this.getReaders();
      return readers.length > 0;
    } catch (error) {
      console.error('[Securgen] Lecteur non disponible:', error);
      return false;
    }
  }

  /**
   * Initialise le lecteur et retourne le lecteur sélectionné
   * Méthode publique pour l'initialisation non-bloquante
   */
  async initializeReader(): Promise<{ success: boolean; reader?: string; error?: string }> {
    try {
      await this.startService();

      const readers = await this.getReaders();

      if (readers.length === 0) {
        return {
          success: false,
          error: 'NO_READER_AVAILABLE',
        };
      }

      if (!this.selectedReader || !readers.includes(this.selectedReader)) {
        await this.selectReader(readers[0]);
      }

      return {
        success: true,
        reader: this.selectedReader ?? readers[0],
      };
    } catch (error) {
      console.error('[Securgen] Erreur initialisation:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'SERVICE_ERROR',
      };
    }
  }

  /**
   * Parse les données de getCardData en champs individuels (format length-prefixed).
   * Format: [0]=nom, [1]=prenoms, [2]=pin, [3]=dateExp, [4]=dateEtab, [5]=type, [6]=profil, [7]=numCarte
   */
  private parseCardDataFields(cardDataBase64: string): {
    nom: string; prenoms: string; pin: string; numCarte: string;
  } | null {
    try {
      const cleaned = cardDataBase64.trim();
      if (!cleaned) return null;

      let rawBytes: Uint8Array;
      try {
        const binaryString = atob(cleaned);
        rawBytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          rawBytes[i] = binaryString.charCodeAt(i);
        }
      } catch {
        return null;
      }

      // Retirer les zéros de padding
      let end = rawBytes.length;
      while (end > 0 && rawBytes[end - 1] === 0) end--;
      if (end < 3) return null;

      const data = rawBytes.slice(0, end);
      const decoder = new TextDecoder('utf-8', { fatal: false });
      const fields: string[] = [];
      let offset = 0;

      while (offset < data.length && fields.length < 8) {
        const fieldLen = data[offset];
        offset += 1;
        if (offset + fieldLen > data.length) break;
        if (fieldLen === 0) {
          fields.push('');
        } else {
          fields.push(decoder.decode(data.slice(offset, offset + fieldLen)).trim());
          offset += fieldLen;
        }
      }

      console.log('[Securgen] Champs carte parsés:', fields);
      console.log(`[Securgen] Nombre de champs: ${fields.length}`);
      console.log(`[Securgen] Champ[0] (nom): "${fields[0] || ''}"`);
      console.log(`[Securgen] Champ[7] (numCarte): "${fields[7] || ''}"`);

      if (fields.length < 3 || !fields[0] || fields[0].length === 0) {
        console.warn('[Securgen] Données carte invalides: moins de 3 champs ou nom vide');
        return null;
      }

      return {
        nom: fields[0] || '',
        prenoms: fields[1] || '',
        pin: fields[2] || '',
        numCarte: fields[7] || '',
      };
    } catch (error) {
      console.error('[Securgen] Erreur parsing getCardData:', error);
      return null;
    }
  }

  /**
   * Vérification LÉGÈRE de la présence carte.
   * Appelle simplement /cardPresent (retourne 0/1/2) sans relancer l'auth.
   * Utilisé pour le polling rapide du statut carte dans l'UI.
   */
  async checkCardPresenceLight(): Promise<{ present: boolean; authenticated: boolean }> {
    try {
      const status = await this.checkCardPresentRaw();
      return {
        present: status >= 1,
        authenticated: status === 2,
      };
    } catch {
      return { present: false, authenticated: false };
    }
  }

  /**
   * Vérifie si une carte est présente (méthode complète avec lecture données)
   * ATTENTION: Appelle authenticateCard (lourd, 3s+). À utiliser uniquement
   * quand on a besoin des données de la carte, pas pour le polling UI.
   */
  async checkCardStatus(): Promise<{ present: boolean; cardNumber?: string; accountNumber?: string }> {
    try {
      // Utiliser authenticateCard pour garantir l'auth mutuelle
      const authResult = await this.authenticateCard();

      if (authResult === 0) {
        return { present: false };
      }

      // Attendre que l'auth mutuelle soit terminée
      const authenticated = await this.waitForCardAuthenticated(10000);

      if (!authenticated) {
        return { present: false };
      }

      // Lire TOUTES les données depuis getCardData (numCarte inclus en champ 8)
      // Plus besoin d'appeler getCardNum séparément
      try {
        const cardDataBase64 = await this.getCardData();
        const parsedData = this.parseCardDataFields(cardDataBase64);

        if (parsedData) {
          return {
            present: true,
            cardNumber: parsedData.numCarte || '',
            accountNumber: parsedData.numCarte || '',
          };
        }
      } catch (error) {
        console.warn('[Securgen] Impossible de lire les données de la carte:', error);
      }

      return { present: false };
    } catch (error) {
      console.error('[Securgen] Erreur vérification carte:', error);
      return { present: false };
    }
  }

  /**
   * Retourne le lecteur actuellement sélectionné
   */
  getSelectedReader(): string | null {
    return this.selectedReader;
  }
}
